import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Modal from "react-modal";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { FaUserPlus, FaEdit, FaCheckCircle, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

Modal.setAppElement("#root");

// メール形式のチェック
const isValidEmail = (str) => /\S+@\S+\.\S+/.test(str);

// パスワードのフォーマットチェック関数
const isValidPassword = (password) => {
  return (
    password.length >= 12 &&
    /[A-Za-z]/.test(password) &&  // 英字が1文字以上
    /\d/.test(password)           // 数字が1文字以上
  );
};

const AdminTeachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [formData, setFormData] = useState({
    teacherid: "",
    name: "",
    email: "",
    department: "",
    password: "",
    approved: false,
  });
  const [editingTeacher, setEditingTeacher] = useState(null);

  // ▼▼▼ 一括登録用 ▼▼▼
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [fileError, setFileError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]); // 行ごとのエラー配列

  // フォームエラー用
  const [formError, setFormError] = useState("");
  // 各処理中のローディング状態
  const [isLoading, setIsLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  // 一括登録中の経過時間とタイマー
  const [bulkElapsedTime, setBulkElapsedTime] = useState(0);
  const [bulkTimer, setBulkTimer] = useState(null);

  // ========================
  //  講師一覧の取得
  // ========================
  const fetchTeachers = async () => {
    try {
      const data = await getRequest("teachers");
      if (data && data.teachers) {
        setTeachers(data.teachers);
      }
    } catch (error) {
      console.error("❌ 教師データ取得エラー:", error);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  // ========================
  //  追加／編集フォーム処理
  // ========================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 新規追加時の処理
  const addTeacher = async () => {
    setIsLoading(true);
    if (!formData.teacherid || !formData.name || !formData.email) {
      setFormError("講師ID、氏名、メールアドレスは必須です");
      setIsLoading(false);
      return;
    }
    // メールアドレス形式チェック
    if (!isValidEmail(formData.email)) {
      setFormError("メールアドレスの形式が不正です。");
      setIsLoading(false);
      return;
    }
    // 講師IDの重複チェック
    if (teachers.find((t) => t.teacherid === formData.teacherid)) {
      setFormError("入力された講師IDは既に使用されています");
      setIsLoading(false);
      return;
    }
    // メールアドレスの重複チェック
    if (teachers.find((t) => t.email === formData.email)) {
      setFormError("入力されたメールアドレスは既に使用されています");
      setIsLoading(false);
      return;
    }
    // パスワード入力がある場合、フォーマットチェック
    if (!isValidPassword(formData.password)) {
      setFormError("パスワードは12文字以上で、英字と数字を1文字以上含む必要があります");
      setIsLoading(false);
      return;
    }
    try {
      const response = await postRequest("teachers", formData);
      if (response.ok) {
        fetchTeachers();
        setFormError("");
        setFormData({
          teacherid: "",
          name: "",
          email: "",
          department: "",
          password: "",
          approved: false,
        });
        toast.success("講師登録が完了しました");
      } else {
        const data = await response.json();
        setFormError(data.error || "講師追加に失敗しました");
      }
    } catch (error) {
      console.error("❌ 教師追加エラー:", error);
      setFormError("講師追加の通信に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (teacher) => {
    setEditingTeacher(teacher.id);
    setFormError("");
    setFormData({
      teacherid: teacher.teacherid,
      name: teacher.name,
      email: teacher.email,
      department: teacher.department,
      password: "",
      approved: teacher.approved,
    });
  };

  // 更新時の処理
  const updateTeacher = async () => {
    setIsLoading(true);
    if (!formData.teacherid || !formData.name || !formData.email) {
      setFormError("講師ID、氏名、メールアドレスは必須です");
      setIsLoading(false);
      return;
    }
    // メールアドレス形式チェック
    if (!isValidEmail(formData.email)) {
      setFormError("メールアドレスの形式が不正です。");
      setIsLoading(false);
      return;
    }
    // 講師IDの重複チェック（更新対象以外）
    if (teachers.find((t) => t.teacherid === formData.teacherid && t.id !== editingTeacher)) {
      setFormError("入力された講師IDは既に使用されています");
      setIsLoading(false);
      return;
    }
    // メールアドレスの重複チェック（更新対象以外）
    if (teachers.find((t) => t.email === formData.email && t.id !== editingTeacher)) {
      setFormError("入力されたメールアドレスは既に使用されています");
      setIsLoading(false);
      return;
    }
    // パスワード入力がある場合のみフォーマットチェック
    if (formData.password && !isValidPassword(formData.password)) {
      setFormError("パスワードは12文字以上で、英字と数字を1文字以上含む必要があります");
      setIsLoading(false);
      return;
    }
    try {
      const response = await putRequest(`teachers/${editingTeacher}`, formData);
      if (response.ok) {
        fetchTeachers();
        setFormError("");
        setEditingTeacher(null);
        setFormData({
          teacherid: "",
          name: "",
          email: "",
          department: "",
          password: "",
          approved: false,
        });
        toast.success("講師情報の更新が完了しました");
      } else {
        const data = await response.json();
        setFormError(data.error || "講師情報の更新に失敗しました");
      }
    } catch (error) {
      console.error("❌ 教師更新エラー:", error);
      setFormError("講師情報更新の通信に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEditing = () => {
    setEditingTeacher(null);
    setFormError("");
    setFormData({
      teacherid: "",
      name: "",
      email: "",
      department: "",
      password: "",
      approved: false,
    });
  };

  // ========================
  //  退職処理
  // ========================
  const retireTeacher = async (id) => {
    try {
      const response = await putRequest(`teachers/${id}`, { retired: true });
      if (response.ok) {
        await postRequest("teacherAssignments", { uid: id, students: {} });
        fetchTeachers();
        toast.success("講師の退職処理が完了しました");
      }
    } catch (error) {
      console.error("❌ 退職処理エラー:", error);
    }
  };

  const deleteTeacher = async (id) => {
    try {
      const response = await deleteRequest(`teachers/${id}`);
      if (response.ok) {
        fetchTeachers();
        toast.success("講師が削除されました");
      } else {
        if (response.status == 403) {
          toast.error("この講師の授業報告書が存在するため、削除できません");
        } else {
          toast.error("講師削除に失敗しました");
        }
      }
    } catch (error) {
      console.error("❌ 講師削除エラー:", error);
      toast.error("講師削除の通信に失敗しました");
    }
  };

  // ========================
  //  一括登録モーダル関連
  // ========================
  const openBulkModal = () => {
    setBulkModalOpen(true);
    setExcelData([]);
    setFileError("");
    setValidationErrors([]);
  };

  const closeBulkModal = () => {
    // 登録中はモーダルを閉じられないようにする
    if (!bulkLoading) {
      setBulkModalOpen(false);
      setExcelData([]);
      setFileError("");
      setValidationErrors([]);
    }
  };

  const handleExcelFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".xls") && !file.name.endsWith(".xlsx")) {
      setFileError("Excelファイル（.xls, .xlsx）を選択してください");
      return;
    }
    setFileError("");
    setValidationErrors([]);
    try {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const ws = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
        console.log("【シート全体】", jsonData);
        const dataRows = jsonData.slice(5).map((row, i) => ({
          rowIndex: i + 6,
          department: row[3] || "",
          teacherid: row[6] || "",
          name: row[7] || "",
          email: row[14] || "",
        }));
        console.log("【パース結果】", dataRows);
        setExcelData(dataRows);
        const errors = [];
        dataRows.forEach((row) => {
          let rowErrors = [];
          if (!row.teacherid) rowErrors.push("講師IDが空です");
          if (!row.name) rowErrors.push("名前が空です");
          if (!row.email) {
            rowErrors.push("メールが空です");
          } else if (!isValidEmail(row.email)) {
            rowErrors.push("メール形式が不正です");
          }
          if (rowErrors.length > 0) {
            errors.push({ rowIndex: row.rowIndex, messages: rowErrors });
          }
        });
        setValidationErrors(errors);
      };
      reader.readAsBinaryString(file);
    } catch (err) {
      console.error("❌ Excel解析エラー:", err);
      setFileError("Excelファイルの解析に失敗しました");
    }
  };

  // 一括登録
  const handleBulkRegister = async () => {
    if (excelData.length === 0) {
      setFileError("アップロードされたデータがありません");
      return;
    }
    if (validationErrors.length > 0) {
      setFileError("データに不備があります。修正してから再度アップロードしてください。");
      return;
    }
    setBulkLoading(true);
    setBulkElapsedTime(0);
    const timer = setInterval(() => {
      setBulkElapsedTime((prev) => prev + 1);
    }, 1000);
    setBulkTimer(timer);
    try {
      const response = await postRequest("teachers/bulk", { teachers: excelData });
      // モーダルは成功またはエラー時に自動で閉じる
      closeBulkModal();
      if (response.ok) {
        fetchTeachers();
        toast.success("一括登録が完了しました");
      } else {
        const data = await response.json();
        console.error("❌ バルク登録APIエラー:", data);
        toast.error(data.error || "一括登録に失敗しました");
      }
    } catch (error) {
      console.error("❌ 一括登録エラー:", error);
      toast.error("一括登録の通信に失敗しました");
      closeBulkModal();
    } finally {
      clearInterval(timer);
      setBulkTimer(null);
      setBulkLoading(false);
    }
  };

  return (
    <div className="p-6 pt-20 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">講師管理</h1>

      {/* 一括登録エラー表示（モーダル外） */}
      {fileError && <div className="text-red-500 mb-4">{fileError}</div>}

      <div className="flex gap-4 mb-6">
        <button
          onClick={openBulkModal}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
        >
          <FaUpload className="inline mr-2" />
          一括登録
        </button>
      </div>

      {/* 新規追加／更新フォーム */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-3">
          {editingTeacher ? "講師を編集" : "新しい講師を追加"}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            name="teacherid"
            value={formData.teacherid}
            onChange={handleChange}
            placeholder="講師ID"
            className="p-2 border rounded"
          />
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="氏名"
            className="p-2 border rounded"
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="メールアドレス"
            className="p-2 border rounded"
          />
          <input
            type="text"
            name="department"
            value={formData.department}
            onChange={handleChange}
            placeholder="部署"
            className="p-2 border rounded"
          />
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="パスワード（更新時は必須ではありません）"
            className="p-2 border rounded"
          />
        </div>
        {formError && <p className="text-red-500 mt-2">{formError}</p>}
        <div className="flex gap-4 mt-3">
          {editingTeacher ? (
            <>
              <button
                onClick={updateTeacher}
                disabled={isLoading}
                className={`px-4 py-2 rounded-lg text-white transition ${isLoading ? "bg-blue-300" : "bg-blue-500 hover:bg-blue-600"}`}
              >
                {isLoading ? "更新中…" : "更新"}
              </button>
              <button
                onClick={cancelEditing}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
              >
                キャンセル
              </button>
            </>
          ) : (
            <button
              onClick={addTeacher}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-white transition ${isLoading ? "bg-green-300" : "bg-green-500 hover:bg-green-600"}`}
            >
              {isLoading ? "追加中…" : (<><FaUserPlus className="inline mr-2" />追加</>)}
            </button>
          )}
        </div>
      </div>

      {/* 現役講師の一覧 */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-3">現役講師一覧</h2>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 border">講師ID</th>
              <th className="p-2 border">氏名</th>
              <th className="p-2 border">メール</th>
              <th className="p-2 border">部署</th>
              <th className="p-2 border">パスワード</th>
              <th className="p-2 border">承認</th>
              <th className="p-2 border">操作</th>
            </tr>
          </thead>
          <tbody>
            {teachers.filter((t) => !t.retired).map((teacher) => (
              <tr key={teacher.id} className="border">
                <td className="p-2 border">{teacher.teacherid}</td>
                <td className="p-2 border">{teacher.name}</td>
                <td className="p-2 border">{teacher.email}</td>
                <td className="p-2 border">{teacher.department}</td>
                <td className="p-2 border">{teacher.plainPassword ? teacher.plainPassword : "-"}</td>
                <td className="p-2 border">
                  {teacher.approved ? <FaCheckCircle className="text-green-500" /> : "未承認"}
                </td>
                <td className="p-2 border flex gap-2">
                  <button
                    onClick={() => startEditing(teacher)}
                    className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm("本当に退職にしますか？（データは削除されず、退職済みとしてマークされます）")) {
                        retireTeacher(teacher.id);
                      }
                    }}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                  >
                    退職
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm("本当に削除しますか？")) {
                        deleteTeacher(teacher.id);
                      }
                    }}
                    className="bg-red-700 text-white px-3 py-1 rounded hover:bg-red-800"
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 退職済み講師の一覧 */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold mb-3">退職済み講師一覧</h2>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 border">講師ID</th>
              <th className="p-2 border">氏名</th>
              <th className="p-2 border">メール</th>
              <th className="p-2 border">部署</th>
              <th className="p-2 border">パスワード</th>
              <th className="p-2 border">操作</th>
            </tr>
          </thead>
          <tbody>
            {teachers.filter((t) => t.retired).map((teacher) => (
              <tr key={teacher.id} className="border">
                <td className="p-2 border">{teacher.teacherid}</td>
                <td className="p-2 border">{teacher.name}</td>
                <td className="p-2 border">{teacher.email}</td>
                <td className="p-2 border">{teacher.department}</td>
                <td className="p-2 border">{teacher.plainPassword ? teacher.plainPassword : "-"}</td>
                <td className="p-2 border flex gap-2">
                  <button
                    onClick={() => {
                      if (window.confirm("本当に削除しますか？")) {
                        deleteTeacher(teacher.id);
                      }
                    }}
                    className="bg-red-700 text-white px-3 py-1 rounded hover:bg-red-800"
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 一括登録モーダル */}
      <Modal
        isOpen={bulkModalOpen}
        onRequestClose={closeBulkModal}
        contentLabel="一括登録モーダル"
        className="bg-white p-4 rounded-lg shadow-md max-w-3xl mx-auto my-20"
        overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center"
      >
        <h2 className="text-xl font-semibold mb-4">講師一括登録</h2>
        <input
          type="file"
          accept=".xls,.xlsx"
          onChange={handleExcelFile}
          className="p-2 border rounded"
        />
        {fileError && <p className="text-red-500 mt-2">{fileError}</p>}
        {excelData.length > 0 && (
          <div className="mt-4 max-h-80 overflow-y-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-2 border">行番号</th>
                  <th className="p-2 border">部署(D)</th>
                  <th className="p-2 border">講師ID(G)</th>
                  <th className="p-2 border">氏名(H)</th>
                  <th className="p-2 border">メール(O)</th>
                  <th className="p-2 border">エラー</th>
                </tr>
              </thead>
              <tbody>
                {excelData.map((row, idx) => {
                  const rowErr = validationErrors.find(err => err.rowIndex === row.rowIndex);
                  return (
                    <tr
                      key={idx}
                      className={rowErr ? "border bg-red-50" : "border bg-white"}
                    >
                      <td className="p-2 border">{row.rowIndex}</td>
                      <td className="p-2 border">{row.department}</td>
                      <td className="p-2 border">{row.teacherid}</td>
                      <td className="p-2 border">{row.name}</td>
                      <td className="p-2 border">{row.email}</td>
                      <td className="p-2 border text-xs text-red-600">
                        {rowErr ? rowErr.messages.map((m, i) => <div key={i}>{m}</div>) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* 進捗表示 */}
        {bulkLoading && (
          <div className="mt-4 text-blue-600 font-semibold">
            登録中… 経過時間: {bulkElapsedTime}秒
          </div>
        )}
        <div className="mt-4 flex gap-4">
          <button
            onClick={handleBulkRegister}
            disabled={bulkLoading}
            className={`bg-green-500 text-white px-4 py-2 rounded-lg transition ${bulkLoading ? "bg-green-300" : "hover:bg-green-600"}`}
          >
            {bulkLoading ? "登録中…" : "登録実行"}
          </button>
          <button
            onClick={closeBulkModal}
            disabled={bulkLoading}
            className={`bg-gray-500 text-white px-4 py-2 rounded-lg transition ${bulkLoading ? "bg-gray-300" : "hover:bg-gray-600"}`}
          >
            キャンセル
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminTeachers;
